import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Printer, X } from "lucide-react";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import { getCurrentPage } from "~/functions";
import api from "~/services/api";
import { toast } from "react-toastify";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";

const CARD_CLASS =
  "rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)]";

const CHART_COLORS = [
  "var(--color-mila_orange)",
  "var(--color-primary)",
  "#94a3b8",
  "#f59e0b",
  "#22c55e",
];

const STARTED_AND_FINISHED_KEY = "Started & Filled Out";
const FINISHED_ONLY_KEY = "Filled Out (other months)";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const isPresentNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

const formatNumber = (value) =>
  isPresentNumber(value) ? value.toLocaleString("pt-BR") : "—";

const formatCurrency = (value) =>
  isPresentNumber(value) ? `$ ${value.toLocaleString("pt-BR")}` : "—";

const formatPercent = (value) =>
  isPresentNumber(value) ? `${value}%` : "—";

const formatMonthShort = (value) => {
  if (!value) return "";
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
};

const normalizeText = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const formatMonthYear = (value) => {
  if (!value) return "";
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().split("T")[0];
};

const formatEmergencyContact = (item) => {
  if (!item) return "—";
  const directValue =
    item.emergency_contact || item.emergencyContact || item.emergency;
  if (typeof directValue === "string") {
    return directValue || "—";
  }
  const contact =
    directValue && typeof directValue === "object" ? directValue : null;
  const name =
    contact?.name ||
    contact?.full_name ||
    item.emergency_contact_name ||
    item.emergency_contact_full_name ||
    item.emergency_name ||
    item.emergencyContactName;
  const phone =
    contact?.phone ||
    contact?.phone_number ||
    item.emergency_contact_phone ||
    item.emergency_phone ||
    item.emergencyContactPhone;
  const email =
    contact?.email ||
    item.emergency_contact_email ||
    item.emergency_email ||
    item.emergencyContactEmail;
  const parts = [name, phone, email].filter(Boolean);
  return parts.length ? parts.join(" | ") : "—";
};

const getSubstatusLabel = (detail) =>
  detail?.name || detail?.substatus || "Unknown";

const buildSubstatusTooltip = (breakdown) => {
  if (!breakdown || breakdown.length === 0) return "";
  return breakdown
    .map((item) => `${getSubstatusLabel(item)}: ${item.total}`)
    .join(" | ");
};

const formatAgentName = (name) => {
  if (!name) return "";
  if (name.length <= 12) return name;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return `${parts[0].slice(0, 10)}.`;
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first} ${last.charAt(0).toUpperCase()}.`;
};

const SHIFT_ORDER = ["Morning", "Afternoon", "Evening", "Full Time"];

const normalizeShiftName = (value) => {
  const normalized = normalizeText(value);
  const hasMorning = /morning/.test(normalized);
  const hasAfternoon = /afternoon/.test(normalized);
  const hasEvening = /evening|night/.test(normalized);
  const shiftCount = [hasMorning, hasAfternoon, hasEvening].filter(Boolean).length;

  if (/full[\s-_]*time/.test(normalized) || shiftCount >= 2) return "Full Time";
  if (hasMorning) return "Morning";
  if (hasAfternoon) return "Afternoon";
  if (hasEvening) return "Evening";
  return (value || "Unknown").toString();
};

const CustomTooltipEnrollments = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const finishedKeys = new Set([STARTED_AND_FINISHED_KEY, FINISHED_ONLY_KEY]);
  const finishedItems = payload.filter((item) => finishedKeys.has(item.name));
  const progressItems = payload.filter(
    (item) => !finishedKeys.has(item.name) && item.dataKey !== "target"
  );
  const finishedTotal = finishedItems.reduce(
    (sum, item) => sum + (item.value || 0),
    0
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-lg">
      <p className="text-xs font-semibold text-gray-700">{label}</p>
      <div className="mt-2 flex flex-col gap-2">
        {progressItems.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-700">
              In Progress
            </p>
            {progressItems.map((item) => (
              <p key={item.dataKey} className="text-xs text-gray-600">
                <span className="font-medium" style={{ color: item.color }}>
                  {item.name}
                </span>
                : {item.value}
              </p>
            ))}
          </div>
        )}
        {progressItems.length > 0 && finishedItems.length > 0 && (
          <div className="border-t border-dashed border-gray-200" />
        )}
        {finishedItems.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-700">
              Filled Out This Month: {finishedTotal}
            </p>
            {finishedItems.map((item) => (
              <p key={item.dataKey} className="text-xs text-gray-600 pl-2">
                <span className="font-medium" style={{ color: item.color }}>
                  {item.name}
                </span>
                : {item.value}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, tooltip, centered }) => (
  <div className={`${CARD_CLASS} flex-1 min-w-[220px] p-4${centered ? " flex flex-col" : ""}`}>
    <div className="flex items-start justify-between gap-3">
      <p className={`text-[11px] font-semibold uppercase tracking-wide text-gray-500${centered ? " w-full text-center" : ""}`}>
        {title}
      </p>
      {tooltip && (
        <span className="text-xs text-gray-400" title={tooltip} aria-label={tooltip}>
          info
        </span>
      )}
    </div>
    <div className={centered ? "flex flex-1 items-center justify-center" : ""}>
      <p className={`text-2xl font-semibold text-primary${centered ? " text-center" : " mt-2"}`}>{value}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  </div>
);

const SmallStatCard = ({ title, value, className = "" }) => (
  <div
    className={`${CARD_CLASS} min-w-[140px] px-4 py-3 text-center ${className}`}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </p>
    <p className="mt-1 text-lg font-semibold text-primary">{value}</p>
  </div>
);

const NoDataMessage = ({ message = "No data available." }) => (
  <p className="text-xs font-medium text-gray-500">{message}</p>
);

export default function CommercialDashboard() {
  const currentPage = getCurrentPage();
  const auth = useSelector((state) => state.auth);
  const { agent: userAgent } = auth?.profile || {};
  const isRestrictVision =
    userAgent?.vision?.toString().toLowerCase() === "restrict";
  const [activeTab, setActiveTab] = useState(0);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedCampus, setSelectedCampus] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [performanceFromMonth, setPerformanceFromMonth] = useState(currentMonth);
  const [performanceFromYear, setPerformanceFromYear] = useState(String(currentYear));
  const [performanceToMonth, setPerformanceToMonth] = useState(currentMonth);
  const [performanceToYear, setPerformanceToYear] = useState(String(currentYear));
  const [performanceReportType, setPerformanceReportType] = useState("date");
  const [filials, setFilials] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [agentPage, setAgentPage] = useState(1);
  const [overviewData, setOverviewData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [funnelProspectsData, setFunnelProspectsData] = useState(null);
  const [selectedFunnelStep, setSelectedFunnelStep] = useState("");
  const funnelRequestId = useRef(0);
  const funnelStepRequestId = useRef(0);
  const currentFunnelFilterKey = useRef("");
  const [salesData, setSalesData] = useState(null);
  const [studentProfileData, setStudentProfileData] = useState(null);
  const [loadingTab, setLoadingTab] = useState(null);
  const [tabErrors, setTabErrors] = useState({});
  const [prospectsPage, setProspectsPage] = useState(1);
  const prospectsPageSize = 8;
  const [showAllConversionModal, setShowAllConversionModal] = useState(false);
  const [showAllFunnelAgentsModal, setShowAllFunnelAgentsModal] = useState(false);
  const [salesTeamPage, setSalesTeamPage] = useState(1);
  const [printingPerformance, setPrintingPerformance] = useState(false);
  const [printingMonthlyPerformance, setPrintingMonthlyPerformance] = useState(false);
  const tablePageSize = 8;
  const agentsPerPage = 10;
  const canSeeAllFilials = auth?.filial?.id === 1;

  const tabs = [
    { id: 0, label: "Overview" },
    { id: 1, label: "Sales Funnel" },
    { id: 2, label: "Sales Team" },
    { id: 3, label: "Student Profile" },
  ];

  const setTabError = (tabId, message) => {
    setTabErrors((prev) => ({ ...prev, [tabId]: message }));
  };

  const fetchFilials = async () => {
    try {
      const response = await api.get("/filials");
      setFilials(response.data?.rows || []);
    } catch (error) {
      setFilials([]);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/commercial-dashboard/agents-simple");
      setAgents(response.data?.rows || []);
    } catch (error) {
      setAgents([]);
    }
  };

  const buildDashboardParams = ({ includePeriod = true } = {}) => {
    const params = {};
    if (includePeriod) {
      if (selectedYear && selectedMonth) {
        params.month = `${selectedYear}-${selectedMonth}`;
      } else if (selectedYear) {
        params.year = selectedYear;
      }
    }
    const campusValue = canSeeAllFilials
      ? selectedCampus
      : String(auth?.filial?.id || "");
    if (campusValue && campusValue !== "all") {
      params.filial_id = campusValue;
    }
    if (isRestrictVision && userAgent?.id) {
      params.agent_id = userAgent.id;
    } else if (selectedAgent !== "all") {
      params.agent_id = selectedAgent;
    }
    return Object.keys(params).length ? params : undefined;
  };

  currentFunnelFilterKey.current = JSON.stringify(
    buildDashboardParams({ includePeriod: false }) || {}
  );

  const buildIndividualPerformancePeriod = () => {
    if (!performanceFromYear || !performanceToYear) return null;
    if (!performanceFromMonth || !performanceToMonth) return null;
    const fromYear = Number(performanceFromYear);
    const toYear = Number(performanceToYear);
    const fromMonth = Number(performanceFromMonth);
    const toMonth = Number(performanceToMonth);
    if (!fromYear || !toYear || !fromMonth || !toMonth) return null;
    const fromDate = new Date(fromYear, fromMonth - 1, 1);
    const toLastDay = new Date(toYear, toMonth, 0).getDate();
    const toDate = new Date(toYear, toMonth - 1, toLastDay);
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return null;
    }
    if (fromDate > toDate) return null;
    return {
      from: `${performanceFromYear}-${performanceFromMonth}-01`,
      to: `${performanceToYear}-${performanceToMonth}-${String(toLastDay).padStart(2, "0")}`,
    };
  };

  const handlePrintIndividualPerformance = async () => {
    const period = buildIndividualPerformancePeriod();
    if (!period || printingPerformance) return;
    setPrintingPerformance(true);
    try {
      const params = new URLSearchParams({
        period_from: period.from,
        period_to: period.to,
      });
      if (performanceReportType === "fee_type") {
        params.set("fee_type", "true");
      }
      const campusValue = canSeeAllFilials
        ? selectedCampus
        : String(auth?.filial?.id || "");
      if (campusValue && campusValue !== "all") {
        params.set("filial_id", campusValue);
      }
      if (isRestrictVision && userAgent?.id) {
        params.set("agent_id", String(userAgent.id));
      } else if (selectedAgent !== "all") {
        params.set("agent_id", selectedAgent);
      }
      const response = await api.get(
        `/commercial-dashboard/agents-finished/pdf?${params.toString()}`,
        { responseType: "blob" }
      );
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `individual_performance_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error generating individual performance PDF:", error);
    } finally {
      setPrintingPerformance(false);
    }
  };

  const handlePrintMonthlyPerformance = async () => {
    if (printingMonthlyPerformance) return;
    setPrintingMonthlyPerformance(true);
    try {
      const params = new URLSearchParams(buildDashboardParams());
      const response = await api.get(
        `/commercial-dashboard/monthly-performance/pdf?${params.toString()}`,
        { responseType: "blob" }
      );
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `monthly_performance_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error generating monthly performance PDF:", error);
    } finally {
      setPrintingMonthlyPerformance(false);
    }
  };

  const fetchOverview = async () => {
    setLoadingTab(0);
    setTabError(0, "");
    try {
      const response = await api.get("/commercial-dashboard", {
        params: buildDashboardParams(),
      });
      setOverviewData(response.data);
    } catch (error) {
      setTabError(0, "Failed to load overview data.");
    } finally {
      setLoadingTab(null);
    }
  };

  const fetchFunnel = async () => {
    const requestId = ++funnelRequestId.current;
    const filterKey = currentFunnelFilterKey.current;
    setLoadingTab(1);
    setTabError(1, "");
    try {
      const response = await api.get(
        "/commercial-dashboard/get-dashboard-funnel",
        { params: buildDashboardParams({ includePeriod: false }) }
      );
      if (requestId !== funnelRequestId.current || filterKey !== currentFunnelFilterKey.current) return;
      setFunnelData(response.data);
      setFunnelProspectsData(null);
      setSelectedFunnelStep("");
    } catch (error) {
      if (requestId !== funnelRequestId.current || filterKey !== currentFunnelFilterKey.current) return;
      setTabError(1, "Failed to load funnel data.");
    } finally {
      if (requestId === funnelRequestId.current && filterKey === currentFunnelFilterKey.current) {
        setLoadingTab(null);
      }
    }
  };

  const fetchFunnelProspectsByStep = async (step) => {
    if (!step) return;
    const requestId = ++funnelStepRequestId.current;
    const filterKey = currentFunnelFilterKey.current;
    setLoadingTab(1);
    setTabError(1, "");
    try {
      const response = await api.get("/commercial-dashboard/form-step", {
        params: {
          ...buildDashboardParams({ includePeriod: false }),
          form_step: step,
        },
      });
      if (requestId !== funnelStepRequestId.current || filterKey !== currentFunnelFilterKey.current) return;
      setFunnelProspectsData(response.data);
      setSelectedFunnelStep(step);
    } catch (error) {
      if (requestId !== funnelStepRequestId.current || filterKey !== currentFunnelFilterKey.current) return;
      setTabError(1, "Failed to load funnel prospects.");
    } finally {
      if (requestId === funnelStepRequestId.current && filterKey === currentFunnelFilterKey.current) {
        setLoadingTab(null);
      }
    }
  };

  const fetchSales = async () => {
    setLoadingTab(2);
    setTabError(2, "");
    try {
      const response = await api.get(
        "/commercial-dashboard/get-dashboard-sales",
        { params: buildDashboardParams() }
      );
      setSalesData(response.data);
    } catch (error) {
      setTabError(2, "Failed to load sales data.");
    } finally {
      setLoadingTab(null);
    }
  };

  const fetchStudentProfile = async () => {
    setLoadingTab(3);
    setTabError(3, "");
    try {
      const response = await api.get(
        "/commercial-dashboard/commercial-student",
        { params: buildDashboardParams() }
      );
      setStudentProfileData(response.data);
    } catch (error) {
      setTabError(3, "Failed to load student profile data.");
    } finally {
      setLoadingTab(null);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 0) {
      fetchOverview();
    } else if (tabId === 1) {
      fetchFunnel();
    } else if (tabId === 2) {
      fetchSales();
    } else if (tabId === 3) {
      fetchStudentProfile();
    }
  };

  useEffect(() => {
    fetchFilials();
    fetchAgents();
    fetchOverview();
  }, []);

  useEffect(() => {
    if (!auth?.filial?.id) return;
    if (!canSeeAllFilials) {
      setSelectedCampus(String(auth.filial.id));
    }
  }, [auth?.filial?.id, canSeeAllFilials]);

  useEffect(() => {
    if (userAgent?.id && isRestrictVision) {
      setSelectedAgent(String(userAgent.id));
    }
  }, [userAgent?.id, isRestrictVision]);

  useEffect(() => {
    if (activeTab === 0) fetchOverview();
    if (activeTab === 1) {
      funnelStepRequestId.current += 1;
      setFunnelData(null);
      setFunnelProspectsData(null);
      setSelectedFunnelStep("");
      fetchFunnel();
    }
    if (activeTab === 2) fetchSales();
    if (activeTab === 3) fetchStudentProfile();
  }, [selectedMonth, selectedYear, selectedCampus, selectedAgent, activeTab]);

  const handleYearChange = (event) => {
    const value = event.target.value;
    setSelectedYear(value);
    if (!value) {
      setSelectedMonth("");
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, index) =>
    String(currentYear - index)
  );

  const overviewMonthData = useMemo(() => {
    if (!overviewData?.enrollments_by_month?.length) {
      return {
        data: [],
        substatusKeys: [],
        hasFinished: false,
        hasTarget: false,
      };
    }

    const substatusKeys = new Set();
    let hasFinished = false;
    let hasTarget = false;
    const monthlyTableByMonth = new Map();
    (overviewData.monthly_table || []).forEach((row) => {
      if (row?.month) {
        monthlyTableByMonth.set(row.month, row);
      }
    });
    const data = overviewData.enrollments_by_month.map((item) => {
      const entry = {
        month: formatMonthShort(item.month),
      };
      const targetValue = isPresentNumber(item.target)
        ? item.target
        : isPresentNumber(item.meta)
        ? item.meta
        : isPresentNumber(item.goal)
        ? item.goal
        : 0;
      entry.target = targetValue;
      hasTarget = true;
      let finishedFromBreakdown = 0;
      (item.substatus_breakdown || []).forEach((detail) => {
        const label = getSubstatusLabel(detail);
        if (normalizeText(label) === "finished") {
          finishedFromBreakdown = detail.total || 0;
          return;
        }
        substatusKeys.add(label);
        entry[label] = detail.total;
      });

      const monthlyRow = monthlyTableByMonth.get(item.month) || {};
      const finishedTotal = isPresentNumber(monthlyRow.finished_this_month)
        ? monthlyRow.finished_this_month
        : finishedFromBreakdown;
      const startedAndFinished = isPresentNumber(
        monthlyRow.started_and_filled_out_this_month
      )
        ? monthlyRow.started_and_filled_out_this_month
        : 0;
      const safeFinishedTotal = Math.max(
        0,
        finishedTotal || 0,
        startedAndFinished || 0
      );
      const safeStartedAndFinished = Math.max(
        0,
        Math.min(startedAndFinished || 0, safeFinishedTotal)
      );
      const finishedOnly = Math.max(0, safeFinishedTotal - safeStartedAndFinished);
      if (safeFinishedTotal || safeStartedAndFinished) {
        hasFinished = true;
      }
      entry[STARTED_AND_FINISHED_KEY] = safeStartedAndFinished;
      entry[FINISHED_ONLY_KEY] = finishedOnly;
      return entry;
    });

    return {
      data,
      substatusKeys: Array.from(substatusKeys),
      hasFinished,
      hasTarget,
    };
  }, [overviewData]);

  const overviewMonthlyTable = useMemo(() => {
    if (!overviewData?.monthly_table?.length) {
      return [];
    }

    return overviewData.monthly_table.map((row) => ({
      month: formatMonthYear(row.month),
      startedThisMonth: row.started_this_month,
      finishedThisMonth: row.finished_this_month,
      startedAndFinishedThisMonth: row.started_and_finished_this_month,
      startedAndFilledOutThisMonth: row.started_and_filled_out_this_month,
      studentsPaid: row.students_paid,
      conversion: isPresentNumber(row.conversion_rate_percent)
        ? row.conversion_rate_percent
        : row.conversion_percent,
      monthlyRevenue: row.monthly_revenue,
    }));
  }, [overviewData]);

  const latestOverviewBreakdown = useMemo(() => {
    if (!overviewData?.enrollments_by_month?.length) return "";
    const lastEntry =
      overviewData.enrollments_by_month[
        overviewData.enrollments_by_month.length - 1
      ];
    return buildSubstatusTooltip(lastEntry.substatus_breakdown);
  }, [overviewData]);

  const funnelStats = useMemo(() => {
    if (!funnelData) {
      return [];
    }

    const stats = [
      {
        title: "Ongoing enrollments",
        value: formatNumber(funnelData.total_enrollments || 0),
      },
    ];

    (funnelData.substatus_funnel || []).forEach((item) => {
      stats.push({
        title: item.form_step || item.substatus,
        value: formatNumber(item.total),
      });
    });

    return stats;
  }, [funnelData]);

  const funnelStages = useMemo(() => {
    if (!funnelData?.substatus_funnel?.length) return [];

    const total = (funnelData.substatus_funnel || []).reduce(
      (sum, item) => sum + (item.total || 0),
      0
    );
    return (funnelData.substatus_funnel || []).map((item) => ({
      stage: item.form_step || item.substatus,
      value: item.total,
      percent: total ? Math.round((item.total / total) * 100) : 0,
    }));
  }, [funnelData]);

  const fullFunnelAgents = useMemo(() => {
    if (!funnelData?.agent_funnel?.length) return [];
    return funnelData.agent_funnel
      .map((item) => ({
        name: item.agent_name,
        enrollments: item.total,
      }))
      .sort((a, b) => b.enrollments - a.enrollments);
  }, [funnelData]);

  const funnelAgents = useMemo(
    () => fullFunnelAgents.slice(0, 5),
    [fullFunnelAgents]
  );

  const funnelProspects = useMemo(() => {
    const source = funnelProspectsData?.prospects || funnelData?.prospects || [];
    if (!source.length) return [];
    return source.map((item) => ({
      prospect: item.prospect_name || item.prospect_id,
      dataEntrada: formatDate(item.entry_date),
      etapa: item.current_stage,
      agent: item.agent_name || "—",
      emergencyContact: formatEmergencyContact(item),
    }));
  }, [funnelData, funnelProspectsData]);

  const totalProspectsPages = Math.max(
    1,
    Math.ceil(funnelProspects.length / prospectsPageSize)
  );

  const paginatedProspects = useMemo(() => {
    const start = (prospectsPage - 1) * prospectsPageSize;
    return funnelProspects.slice(start, start + prospectsPageSize);
  }, [funnelProspects, prospectsPage]);

  useEffect(() => {
    setProspectsPage((prev) =>
      Math.min(Math.max(1, prev), totalProspectsPages)
    );
  }, [totalProspectsPages]);

  useEffect(() => {
    setProspectsPage(1);
  }, [selectedFunnelStep]);

  const salesTopPerformer = useMemo(() => {
    if (!salesData?.top_performer) {
      return {
        agent_name: "—",
        finished_enrollments: null,
      };
    }
    return salesData.top_performer;
  }, [salesData]);

  const salesEnrollmentsByAgent = useMemo(() => {
    if (!salesData?.enrollments_by_agent?.length) return [];
    return salesData.enrollments_by_agent
      .map((item) => ({
        name: item.agent_name,
        enrollments: item.total,
      }))
      .sort((a, b) => b.enrollments - a.enrollments)
      .slice(0, 5);
  }, [salesData]);

  const fullSalesConversionByAgent = useMemo(() => {
    if (!salesData?.conversion_rate_by_agent?.length) return [];
    return salesData.conversion_rate_by_agent.map((item) => ({
      name: item.agent_name,
      conversao: item.conversion_percent,
      prospects: item.prospects,
      finishedEnrollments: item.finished_enrollments,
    }));
  }, [salesData]);

  const salesConversionByAgent = useMemo(
    () =>
      fullSalesConversionByAgent
        .slice()
        .sort((a, b) => b.conversao - a.conversao)
        .slice(0, 5),
    [fullSalesConversionByAgent]
  );

  const salesTableRows = useMemo(() => {
    if (!salesData?.conversion_rate_by_agent?.length) {
      return [];
    }

    return salesData.conversion_rate_by_agent.map((row) => ({
      name: row.agent_name,
      prospects: row.prospects,
      finishedEnrollments: row.finished_enrollments,
      conversion: row.conversion_percent,
    }));
  }, [salesData]);

  const salesTeamTotalPages = Math.max(
    1,
    Math.ceil(salesTableRows.length / tablePageSize)
  );

  const paginatedSalesTeamRows = useMemo(() => {
    const start = (salesTeamPage - 1) * tablePageSize;
    return salesTableRows.slice(start, start + tablePageSize);
  }, [salesTableRows, salesTeamPage]);

  useEffect(() => {
    setSalesTeamPage((prev) =>
      Math.min(Math.max(1, prev), salesTeamTotalPages)
    );
  }, [salesTeamTotalPages]);

  const ageData = useMemo(() => {
    if (!studentProfileData?.enrollments_by_age_group?.length) {
      return [];
    }
    return studentProfileData.enrollments_by_age_group.map((item) => ({
      name: item.group,
      value: item.total,
    }));
  }, [studentProfileData]);

  const processTypeData = useMemo(() => {
    if (!studentProfileData?.enrollments_by_process_type?.length) {
      return [];
    }
    return studentProfileData.enrollments_by_process_type.map((item) => ({
      type: item.name || item.type || "Unknown",
      value: item.total,
    }));
  }, [studentProfileData]);

  const shiftData = useMemo(() => {
    if (!studentProfileData?.enrollments_by_shift?.length) {
      return [];
    }
    const totalsByShift = {};

    studentProfileData.enrollments_by_shift.forEach((item) => {
      const shift = normalizeShiftName(item?.shift);
      const total = Number(item?.total) || 0;
      totalsByShift[shift] = (totalsByShift[shift] || 0) + total;
    });

    const orderedData = SHIFT_ORDER.map((shift) => ({
      shift,
      value: totalsByShift[shift] || 0,
    }));
    const extraShifts = Object.keys(totalsByShift)
      .filter((shift) => !SHIFT_ORDER.includes(shift))
      .map((shift) => ({ shift, value: totalsByShift[shift] }));

    return [...orderedData, ...extraShifts];
  }, [studentProfileData]);

  const selectedAgentLabel = useMemo(() => {
    if (isRestrictVision && userAgent?.id) {
      return userAgent.name || "Agent";
    }
    if (selectedAgent === "all") return "All Agents";
    const match = agents.find(
      (agent) => String(agent.agent_id) === String(selectedAgent)
    );
    return match?.agent_name || "Select agent";
  }, [agents, selectedAgent, userAgent?.id, userAgent?.name, isRestrictVision]);

  const filteredAgents = useMemo(() => {
    const term = normalizeText(agentSearch);
    if (!term) return agents;
    return agents.filter((agent) =>
      normalizeText(agent.agent_name).includes(term)
    );
  }, [agents, agentSearch]);

  const paginatedAgents = useMemo(() => {
    const total = filteredAgents.length;
    const pages = Math.max(1, Math.ceil(total / agentsPerPage));
    const safePage = Math.min(Math.max(1, agentPage), pages);
    const start = (safePage - 1) * agentsPerPage;
    const end = start + agentsPerPage;
    return {
      total,
      pages,
      page: safePage,
      rows: filteredAgents.slice(start, end),
    };
  }, [filteredAgents, agentPage, agentsPerPage]);

  useEffect(() => {
    setAgentPage(1);
  }, [agentSearch]);

  useEffect(() => {
    if (!agentModalOpen) {
      setAgentSearch("");
      setAgentPage(1);
    }
  }, [agentModalOpen]);

  const handleAgentSelect = (value) => {
    setSelectedAgent(value);
    setAgentModalOpen(false);
  };

  const handleCampusChange = (event) => {
    const value = event.target.value;
    if (!canSeeAllFilials) {
      setSelectedCampus(String(auth?.filial?.id || ""));
      return;
    }
    setSelectedCampus(value);
  };

  const campusOptions = useMemo(() => {
    if (canSeeAllFilials) return filials;
    if (!auth?.filial?.id) return [];
    return [{ id: auth.filial.id, name: auth.filial.name }];
  }, [filials, auth?.filial?.id, auth?.filial?.name, canSeeAllFilials]);

  const renderVisaoGeral = () => (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        <StatCard
          title="Enrollments Started This Month"
          value={formatNumber(overviewData?.enrollments_started)}
          subtitle=""
          centered
        />
        <div className={`${CARD_CLASS} flex-1 min-w-[220px] p-4 flex flex-col gap-3`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 text-center">
            Enrollments Filled Out This Month
          </p>
          <p className="text-3xl font-bold text-primary text-center">
            {formatNumber(
              (overviewData?.finished_previous_months ?? 0) +
                (overviewData?.enrollments_started_and_finished ?? 0)
            )}
          </p>
          <div className="flex border-t pt-2 gap-2">
            <div className="flex-1 text-center">
              <p className="text-lg font-semibold text-primary">
                {formatNumber(overviewData?.enrollments_started_and_finished)}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Started this Month
              </p>
            </div>
            <div className="w-px bg-gray-200" />
            <div className="flex-1 text-center">
              <p className="text-lg font-semibold text-primary">
                {formatNumber(overviewData?.finished_previous_months)}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Started Previous Month
              </p>
            </div>
          </div>
        </div>
        <StatCard
          title="Students Paid This Month"
          value={
            <span className="flex items-center justify-center gap-2">
              <span>{formatNumber(overviewData?.students_paid_this_month)}</span>
              <span className="text-gray-400">-</span>
              <span>{formatCurrency(overviewData?.monthly_revenue)}</span>
            </span>
          }
          subtitle=""
          centered
        />
        <StatCard
          title="Enrollments Filled Out this year"
          value={formatNumber(overviewData?.enrollments_finished_this_year)}
          subtitle=""
          centered
        />
        <StatCard
          title="Students Paid This Year"
          value={
            <span className="flex items-center justify-center gap-2">
              <span>{formatNumber(overviewData?.students_paid_this_year)}</span>
              <span className="text-gray-400">-</span>
              <span>{formatCurrency(overviewData?.year_revenue)}</span>
            </span>
          }
          centered
          subtitle=""
        />
        {/* <SmallStatCard
          title="Conversion Rate"
          value={formatPercent(overviewData?.conversion_rate_percent)}
        /> */}
      </div>

      <div className="flex flex-wrap gap-4">
        <div className={`${CARD_CLASS} w-full p-4`} style={{ minWidth: 360 }}>
          <h3 className="text-sm font-semibold text-gray-700">
            Enrollment Started and Filled Out by Month x Target
          </h3>
          <div className="mt-4" style={{ height: 280 }}>
            {overviewMonthData.data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={overviewMonthData.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
                  <Tooltip content={<CustomTooltipEnrollments />} />
                  {overviewMonthData.substatusKeys.map((key, index) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="progress"
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      name={key}
                      radius={
                        index === overviewMonthData.substatusKeys.length - 1
                          ? [4, 4, 0, 0]
                          : [0, 0, 0, 0]
                      }
                    />
                  ))}
                  {overviewMonthData.hasFinished && (
                    <>
                      <Bar
                        dataKey={STARTED_AND_FINISHED_KEY}
                        stackId="finished"
                        fill={CHART_COLORS[4]}
                        name="Started & Filled Out"
                        radius={[0, 0, 0, 0]}
                      />
                      <Bar
                        dataKey={FINISHED_ONLY_KEY}
                        stackId="finished"
                        fill={CHART_COLORS[2]}
                        name="Filled Out (other months)"
                        radius={[4, 4, 0, 0]}
                      />
                    </>
                  )}
                  {overviewMonthData.hasTarget && (
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke={CHART_COLORS[1]}
                      strokeWidth={2}
                      name="Target"
                      dot={{ fill: CHART_COLORS[1], strokeWidth: 2 }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>
      </div>

      <div className={`${CARD_CLASS} p-4`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            Monthly Performance
          </h3>
          <button
            type="button"
            onClick={handlePrintMonthlyPerformance}
            disabled={printingMonthlyPerformance}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-mila_orange hover:text-mila_orange disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Printer size={14} />
            PRINT
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          {overviewMonthlyTable.length > 0 ? (
            <table className="w-full border-collapse text-xs">
              <thead className="bg-secondary-50 text-gray-600">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold">Month</th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Started (Month)
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Filled Out (Month)
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Started &amp; Filled Out (Month)
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Students Paid
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Conversion
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Monthly Revenue
                  </th>
                </tr>
              </thead>
              <tbody>
                {overviewMonthlyTable.map((row, index) => (
                  <tr
                    key={row.month}
                    className={`border-b border-gray-100 ${
                      index % 2 === 0 ? "bg-white" : "bg-secondary-50"
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-700">{row.month}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.startedThisMonth)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.finishedThisMonth)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.startedAndFilledOutThisMonth)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.studentsPaid)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="rounded-full bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary">
                        {formatPercent(row.conversion)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatCurrency(row.monthlyRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoDataMessage />
          )}
        </div>
      </div>
    </div>
  );

  const renderFunnelVendas = () => (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        {funnelStats.length > 0 ? (
          funnelStats.map((stat) => (
            <SmallStatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
            />
          ))
        ) : (
          <NoDataMessage />
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 320 }}>
          <h3 className="text-sm font-semibold text-gray-700">
            Conversion Funnel
          </h3>
          <div className="mt-4 flex flex-col gap-2">
            {funnelStages.length > 0 ? (
              funnelStages.map((item, index) => (
                <button
                  key={item.stage}
                  type="button"
                  onClick={() => fetchFunnelProspectsByStep(item.stage)}
                  className={`flex w-full items-center gap-3 rounded-md p-1 text-left transition ${
                    selectedFunnelStep === item.stage
                      ? "bg-secondary-50"
                      : "hover:bg-secondary-50"
                  }`}
                >
                  <span className="w-40 text-xs font-medium text-gray-600">
                    {item.stage}
                  </span>
                  <div className="flex-1">
                    <div
                      className="flex items-center justify-end rounded-md px-3 py-2 text-xs text-white"
                      style={{
                        width: `${Math.max(15, item.percent || 0)}%`,
                        backgroundColor:
                          index % 2 === 0 ? CHART_COLORS[0] : CHART_COLORS[1],
                      }}
                    >
                      <span className="font-semibold">
                        {item.value} ({item.percent}%)
                      </span>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>

        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 320 }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              Enrollments by Agent
            </h3>
            <button
              type="button"
              onClick={() => setShowAllFunnelAgentsModal(true)}
              className="text-xs font-semibold text-mila_orange hover:underline"
            >
              View more
            </button>
          </div>
          <div className="mt-4" style={{ height: 320 }}>
            {funnelAgents.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelAgents} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    width={120}
                    tickFormatter={formatAgentName}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="enrollments"
                    fill={CHART_COLORS[0]}
                    radius={[0, 4, 4, 0]}
                    name="Enrollments"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>
      </div>

      <div className={`${CARD_CLASS} p-4`}>
        <h3 className="text-sm font-semibold text-gray-700">
          Active Prospects
        </h3>
        <div className="mt-4 overflow-x-auto">
          {paginatedProspects.length > 0 ? (
            <table className="w-full border-collapse text-xs">
              <thead className="bg-secondary-50 text-gray-600">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold">
                    Prospect
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Entry Date
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Current Stage
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Emergency Contact
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Agent</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProspects.map((row, index) => (
                  <tr
                    key={`${row.prospect}-${row.dataEntrada}`}
                    className={`border-b border-gray-100 ${
                      index % 2 === 0 ? "bg-white" : "bg-secondary-50"
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-700">{row.prospect}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.dataEntrada}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary">
                        {row.etapa}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.emergencyContact}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.agent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoDataMessage />
          )}
        </div>
        {paginatedProspects.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
            <span>
              Page {prospectsPage} of {totalProspectsPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setProspectsPage(1)}
                disabled={prospectsPage === 1}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                First
              </button>
              <button
                type="button"
                onClick={() =>
                  setProspectsPage((prev) => Math.max(1, prev - 1))
                }
                disabled={prospectsPage === 1}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setProspectsPage((prev) =>
                    Math.min(totalProspectsPages, prev + 1)
                  )
                }
                disabled={prospectsPage === totalProspectsPages}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setProspectsPage(totalProspectsPages)}
                disabled={prospectsPage === totalProspectsPages}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {showAllFunnelAgentsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h4 className="text-lg font-bold text-gray-800">
                Enrollments by Agent
              </h4>
              <button
                type="button"
                onClick={() => setShowAllFunnelAgentsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <p className="text-sm text-gray-600">
                Showing {fullFunnelAgents.length} agents
              </p>
            </div>
            <div className="p-4 overflow-y-auto">
              {fullFunnelAgents.length > 0 ? (
                <table className="w-full border-collapse text-xs">
                  <thead className="bg-secondary-50 text-gray-600">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-semibold">
                        Agent
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Enrollments
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullFunnelAgents.map((row, index) => (
                      <tr
                        key={`${row.name}-${index}`}
                        className={`border-b border-gray-100 ${
                          index % 2 === 0 ? "bg-white" : "bg-secondary-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-gray-700">{row.name}</td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {formatNumber(row.enrollments)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <NoDataMessage />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderVendedores = () => (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        <div
          className="flex-1 rounded-xl bg-mila_orange px-4 py-4 text-white shadow-sm"
          style={{ minWidth: 240 }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide opacity-90">
            Top Performer
          </p>
          <p className="mt-2 text-xl font-semibold">
            {salesTopPerformer.agent_name}
          </p>
          <p className="mt-1 text-xs opacity-90">
            {formatNumber(salesTopPerformer.finished_enrollments)} filled out in
            this period
          </p>
        </div>
        <StatCard
          title="Team Conversion"
          value={formatPercent(salesData?.team_conversion_percent)}
          subtitle="Team average"
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(salesData?.total_revenue)}
          subtitle="Generated by the team"
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 320 }}>
          <h3 className="text-sm font-semibold text-gray-700">
            Enrollments by Agent
          </h3>
          <div className="mt-4" style={{ height: 280 }}>
            {salesEnrollmentsByAgent.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesEnrollmentsByAgent} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    width={120}
                    tickFormatter={formatAgentName}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="enrollments"
                    fill={CHART_COLORS[0]}
                    radius={[0, 4, 4, 0]}
                    name="Enrollments"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>

        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 320 }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              Conversion Rate by Agent
            </h3>
            <button
              type="button"
              onClick={() => setShowAllConversionModal(true)}
              className="text-xs font-semibold text-mila_orange hover:underline"
            >
              View more
            </button>
          </div>
          <div className="mt-4" style={{ height: 280 }}>
            {salesConversionByAgent.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesConversionByAgent} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    unit="%"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    width={100}
                  />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar
                    dataKey="conversao"
                    fill={CHART_COLORS[1]}
                    radius={[0, 4, 4, 0]}
                    name="Conversion"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>
      </div>

      {showAllConversionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h4 className="text-lg font-bold text-gray-800">
                Conversion Rate by Agent
              </h4>
              <button
                type="button"
                onClick={() => setShowAllConversionModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <p className="text-sm text-gray-600">
                Showing {fullSalesConversionByAgent.length} agents
              </p>
            </div>
            <div className="p-4 overflow-y-auto">
              {fullSalesConversionByAgent.length > 0 ? (
                <table className="w-full border-collapse text-xs">
                  <thead className="bg-secondary-50 text-gray-600">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-semibold">
                        Agent
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Prospects
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Finished
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Conversion
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullSalesConversionByAgent.map((row, index) => (
                      <tr
                        key={`${row.name}-${index}`}
                        className={`border-b border-gray-100 ${
                          index % 2 === 0 ? "bg-white" : "bg-secondary-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-gray-700">{row.name}</td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {formatNumber(row.prospects)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {formatNumber(row.finishedEnrollments)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {formatPercent(row.conversao)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <NoDataMessage />
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`${CARD_CLASS} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Individual Performance
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="font-semibold">From</span>
              <select
                value={performanceFromMonth}
                onChange={(e) => setPerformanceFromMonth(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              >
                {MONTH_NAMES.map((name, index) => {
                  const value = String(index + 1).padStart(2, "0");
                  return (
                    <option key={`from-${value}`} value={value}>
                      {name}
                    </option>
                  );
                })}
              </select>
              <select
                value={performanceFromYear}
                onChange={(e) => setPerformanceFromYear(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              >
                {yearOptions.map((year) => (
                  <option key={`from-${year}`} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <span className="font-semibold">To</span>
              <select
                value={performanceToMonth}
                onChange={(e) => setPerformanceToMonth(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              >
                {MONTH_NAMES.map((name, index) => {
                  const value = String(index + 1).padStart(2, "0");
                  return (
                    <option key={`to-${value}`} value={value}>
                      {name}
                    </option>
                  );
                })}
              </select>
              <select
                value={performanceToYear}
                onChange={(e) => setPerformanceToYear(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              >
                {yearOptions.map((year) => (
                  <option key={`to-${year}`} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                value={performanceReportType}
                onChange={(e) => setPerformanceReportType(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              >
                <option value="date">Date</option>
                <option value="fee_type">Fee Type</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={handlePrintIndividualPerformance}
            disabled={printingPerformance || !buildIndividualPerformancePeriod()}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-mila_orange hover:text-mila_orange disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Printer size={14} />
            PRINT
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          {paginatedSalesTeamRows.length > 0 ? (
            <table className="w-full border-collapse text-xs">
              <thead className="bg-secondary-50 text-gray-600">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold">
                    Sales Rep
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Prospects
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Finished Enrollments
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Conversion
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedSalesTeamRows.map((row, index) => (
                  <tr
                    key={row.name}
                    className={`border-b border-gray-100 ${
                      index % 2 === 0 ? "bg-white" : "bg-secondary-50"
                    }`}
                  >
                    <td className="px-4 py-3 text-gray-700">{row.name}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.prospects)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {formatNumber(row.finishedEnrollments)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="rounded-full bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary">
                        {formatPercent(row.conversion)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoDataMessage />
          )}
        </div>
        {paginatedSalesTeamRows.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
            <span>
              Page {salesTeamPage} of {salesTeamTotalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSalesTeamPage(1)}
                disabled={salesTeamPage === 1}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                First
              </button>
              <button
                type="button"
                onClick={() =>
                  setSalesTeamPage((prev) => Math.max(1, prev - 1))
                }
                disabled={salesTeamPage === 1}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setSalesTeamPage((prev) =>
                    Math.min(salesTeamTotalPages, prev + 1)
                  )
                }
                disabled={salesTeamPage === salesTeamTotalPages}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setSalesTeamPage(salesTeamTotalPages)}
                disabled={salesTeamPage === salesTeamTotalPages}
                className="rounded-md border border-gray-200 px-2 py-1 text-gray-600 disabled:opacity-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPerfilAlunos = () => (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 300 }}>
          <h3 className="text-sm font-semibold text-gray-700">
          Enrollments by Age Group
          </h3>
          <div className="mt-4" style={{ height: 280 }}>
            {ageData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={ageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {ageData.map((entry, index) => (
                      <Cell key={entry.name} fill={CHART_COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>

        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 300 }}>
          <h3 className="text-sm font-semibold text-gray-700">
          Enrollments by Process Type
          </h3>
          <div className="mt-4" style={{ height: 280 }}>
            {processTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="type"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                    name="Quantidade"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>

        <div className={`${CARD_CLASS} flex-1 p-4`} style={{ minWidth: 300 }}>
          <h3 className="text-sm font-semibold text-gray-700">
          Enrollments by Shift
          </h3>
          <div className="mt-4" style={{ height: 280 }}>
            {shiftData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={shiftData}
                  margin={{ left: 16, right: 16, top: 5, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="shift"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                    interval={0}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[1]}
                    radius={[4, 4, 0, 0]}
                    name="Quantidade"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <NoDataMessage />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-hidden">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar />
      </PageHeader>

      <div className="flex w-full flex-wrap items-center gap-3 pb-4">
        {activeTab !== 1 && (
          <>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
            >
              <option value="">All Years</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={!selectedYear}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">All Months</option>
              {MONTH_NAMES.map((name, index) => {
                const value = String(index + 1).padStart(2, "0");
                return (
                  <option key={value} value={value}>
                    {name}
                  </option>
                );
              })}
            </select>
          </>
        )}

        <select
          value={selectedCampus}
          onChange={handleCampusChange}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
        >
          {canSeeAllFilials && <option value="all">All Campuses</option>}
          {campusOptions.map((filial) => (
            <option key={filial.id} value={String(filial.id)}>
              {filial.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setAgentModalOpen(true)}
          disabled={isRestrictVision}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange disabled:opacity-60"
        >
          <span className="flex-1 text-left">{selectedAgentLabel}</span>
          <span className="text-gray-400">v</span>
        </button>
      </div>

      <div className="flex w-full flex-wrap gap-2 border-b border-gray-200 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            className={`rounded-md border px-3 py-2 text-xs font-semibold transition ${
              activeTab === tab.id
                ? "border-mila_orange bg-mila_orange text-white"
                : "border-gray-200 bg-secondary text-gray-600 hover:border-mila_orange hover:text-mila_orange"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(loadingTab === activeTab || tabErrors[activeTab]) && (
        <div className="pt-3">
          {loadingTab === activeTab && (
            <p className="text-xs font-medium text-gray-500">
              Loading data...
            </p>
          )}
          {tabErrors[activeTab] && (
            <p className="text-xs font-medium text-red-500">
              {tabErrors[activeTab]}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 w-full overflow-y-auto pt-6">
        <div className="w-full pb-6" style={{ minWidth: 1200 }}>
          {activeTab === 0 && renderVisaoGeral()}
          {activeTab === 1 && renderFunnelVendas()}
          {activeTab === 2 && renderVendedores()}
          {activeTab === 3 && renderPerfilAlunos()}
        </div>
      </div>

      {agentModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h4 className="text-lg font-bold text-gray-800">Select Agent</h4>
              <button
                type="button"
                onClick={() => setAgentModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Showing {paginatedAgents.total} agents
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Search agent..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    className="w-full sm:w-80 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mila_orange"
                  />
                  <button
                    type="button"
                    onClick={() => handleAgentSelect("all")}
                    className="rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-mila_orange hover:text-mila_orange"
                  >
                    All Agents
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full min-w-[600px] border-collapse">
                <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Agent
                    </th>
                    <th className="text-center p-3 text-sm font-semibold text-gray-700">
                      Select
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAgents.rows.length === 0 && (
                    <tr>
                      <td colSpan={2} className="p-6 text-center text-gray-500">
                        No agents found.
                      </td>
                    </tr>
                  )}
                  {paginatedAgents.rows.map((agent, index) => (
                    <tr
                      key={`${agent.agent_id}-${index}`}
                      className={`border-b transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } hover:bg-orange-50`}
                    >
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                        {agent.agent_name}
                      </td>
                      <td className="p-3 text-sm text-gray-700 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() =>
                            handleAgentSelect(String(agent.agent_id))
                          }
                          className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:border-mila_orange hover:text-mila_orange"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {paginatedAgents.total > 0 && (
              <div className="flex items-center justify-between p-4 border-t bg-gray-50 flex-shrink-0">
                <div className="text-sm text-gray-600">
                  Page {paginatedAgents.page} of {paginatedAgents.pages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAgentPage((prev) => Math.max(1, prev - 1))}
                    disabled={paginatedAgents.page === 1}
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setAgentPage((prev) =>
                        Math.min(paginatedAgents.pages, prev + 1)
                      )
                    }
                    disabled={paginatedAgents.page === paginatedAgents.pages}
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
